// ink-component
import React, { MouseEvent, useEffect } from 'react';
import styled, { ThemeProvider } from 'styled-components';
import { LegacyVariants, FeedbackVariants } from '../_common/Types/GlobalTypes';

import Box from '../Box/Box';
import HStack from '../HStack/HStack';
import Icon from '../Icon/Icon';
import Ornament from '../Ornament/Ornament';
import Text from '../Typography/Text/Text';

import { withAlertContext } from '../../contexts/AlertContext';
import isDevelopmentEnvironment from '../../helpers/isDevelopmentEnvironment';

import getGlobalProps from '../../helpers/constants/global-props';

import {
  alertBase,
  alertVariants,
  AlertAction,
  AlertDismissAction,
  AlertOrnamentWrapper,
  AlertContentWrapper,
} from './styles';

export const ALERT_TIMEOUT = 4000;

type ActionType = {
  'data-testid'?: string | number;
  href?: string;
  target?: string;
  onClick?: (event: MouseEvent<HTMLAnchorElement | HTMLButtonElement>) => void;
  text: string;
};

export interface AlertProps extends React.HTMLAttributes<HTMLDivElement> {
  /** Unique identifier */
  id: string;
  /** Specifies what the alert says */
  text: React.ReactNode;
  /** Semantic variants, based on intent */
  variant?: Extract<
    FeedbackVariants,
    'feedback-negative' | 'feedback-positive' | 'feedback-notice' | 'feedback-pending'
  >;
  /** Type modifier. **Don't use error alerts!** See note on using `error` above */
  /** @deprecated:variant This prop is deprecated and should not be used.  */
  type?: Extract<LegacyVariants, 'success' | 'warning' | 'download' | 'generating' | 'error'>;
  /** If it stays or not */
  isPersistent?: boolean;
  /** Adds a button with a possible action. Use only when Alert isPersistent.  */
  action?: ActionType;
  /** Marks the element with a given ID to make it easier to select in tests */
  'data-testid'?: string | number;
  /** @ignore */
  removeAlert?: () => void;
}

let globalProps = getGlobalProps({});

const StyledAlert = styled.div`
  ${alertBase};
  ${({ theme }) => theme.alertVariant && alertVariants[theme.alertVariant]};
`;

const alertTypesMap = {
  success: 'feedback-positive',
  warning: 'feedback-notice',
  error: 'feedback-negative',
  download: 'feedback-positive',
  generating: 'feedback-loading',
} as const;

/**
 * @purpose Serves as temporary or permanent feedback for a user taking an action, such as saving or downloading
 * @categories accessory
 * @aka notification toaster
 * @HTMLType HTMLDivElement
 */

export function Alert(props: AlertProps) {
  const { action, isPersistent, removeAlert, text, variant, type, style, className, ...otherProps } = props;

  if (type) {
    globalProps = getGlobalProps({ deprecatedProps: true });
  }

  if (type) {
    console.warn(
      '[INK DEPRECATION WARNING]: The `type` prop in Alert is deprecated. Use `variant` instead. https://ink.carta.com/components/Alert/usage',
    );
  }

  if (action && !isPersistent) {
    console.warn(
      '[INK WARNING]: Alert `action` buttons in Alert can only be used when isPersistent is true. https://ink.carta.com/components/Alert/usage',
    );
  }

  useEffect(() => {
    const timeout = setTimeout(() => {
      if (!isPersistent && removeAlert) removeAlert();
    }, ALERT_TIMEOUT);

    if (isDevelopmentEnvironment() && type === 'error') {
      const message =
        'Do not use error alerts on their own, as they disappear too quickly for a user to read. ' +
        'Use a <Banner> for page-level errors or the error property of a <Field> for inline errors.';
      console.warn(message);
    }

    return () => {
      clearTimeout(timeout);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  let ornamentVariant;

  /*
    Alert has a legacy `type` prop that has not been fully migrated to
    `variant`, so we need to map the old types to the new variants.

    Ornament has a new `feedback-loading` variant that spins, while Alert has a
    variant called `feedback-pending` that renders the spinning Ornament, so we
    need to map the latter to the former
  */
  if (type) {
    ornamentVariant = alertTypesMap[type];
  } else if (variant === 'feedback-pending') {
    ornamentVariant = 'feedback-loading';
  } else {
    ornamentVariant = variant;
  }

  return (
    <ThemeProvider
      theme={currentTheme => ({
        ...currentTheme,
        alertIsPersistent: isPersistent,
        alertVariant: ornamentVariant,
      })}
    >
      <StyledAlert {...globalProps} {...otherProps}>
        <AlertContentWrapper>
          <AlertOrnamentWrapper>
            <Ornament variant={ornamentVariant} size="small" />
          </AlertOrnamentWrapper>

          <HStack align="distributed" alignY="center" spacing="medium">
            {Boolean(text) && (
              <Text data-testid="alert-text" trim>
                {text}
              </Text>
            )}
            {action &&
              isPersistent &&
              (action.href ? (
                <AlertAction {...action} href={action.href} as="a">
                  {action.text}
                </AlertAction>
              ) : (
                <AlertAction {...action}>{action.text}</AlertAction>
              ))}
          </HStack>
        </AlertContentWrapper>

        {isPersistent && Boolean(removeAlert) && (
          <AlertDismissAction onClick={removeAlert}>
            <Icon type="close" meaning="close alert" size="lg" />
          </AlertDismissAction>
        )}
      </StyledAlert>

      {/* Originally Snackbar had `margin-bottom: 19px` so if the Box becomes an issue (uses padding), we can revert! */}
      <Box bottom="large" />
    </ThemeProvider>
  );
}

Alert.defaultProps = {
  variant: 'feedback-positive',
  isPersistent: false,
  action: undefined,
  'data-testid': undefined,
};

const composed = withAlertContext(Alert);

// @ts-expect-error
composed.displayName = 'Alert';

Alert.displayName = 'Alert';

export default composed;
